import { useCallback, useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { ImagePlus } from 'lucide-react';
import { useSessionContext } from '../../contexts/SessionContext';
import { useI18n } from '../../i18n';
import { useStudioCommands } from './useStudioCommands';
import { IconButton } from '../ui/icon-button';

/** The image types the upload route accepts (server/src/routes/upload.router.ts). */
const ACCEPT = 'image/png,image/jpeg,image/gif,image/webp';

/**
 * Where an upload of this map's image stands. Nothing is reported done before
 * the server's map:changed for this map and this file: «uploading» is the
 * file on its way, «saving» the map:set awaiting its answer.
 */
export type MapImageUploadState =
  | { readonly status: 'idle' }
  | { readonly status: 'uploading' | 'saving' | 'offline'; readonly fileName: string }
  | { readonly status: 'failed'; readonly fileName: string; readonly error: string };

/**
 * One map's image upload in GM Studio: the file goes to /api/upload (GM only),
 * then map:set on THIS page — the same two steps as the table's «Upload map»,
 * but bound to the page of the pane, not to the table's view, and confirmed by
 * the server's echo. A lost map:set answer is re-sent after the rejoin (the
 * same image again changes nothing); a refusal is shown here. Players learn
 * the image only when they see this map (server: map:changed to GMs only for
 * any other page).
 */
export function useMapImageUpload(pageId: string): {
  readonly state: MapImageUploadState;
  readonly upload: (file: File) => void;
} {
  const send = useMapImageSender();
  const [state, setState] = useState<MapImageUploadState>({ status: 'idle' });
  const busyRef = useRef(false);
  // An unmounted pane (or another page in it) voids a late answer.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return (): void => {
      aliveRef.current = false;
    };
  }, []);

  const settle = useCallback((next: MapImageUploadState): void => {
    busyRef.current = next.status === 'uploading' || next.status === 'saving' || next.status === 'offline';
    if (aliveRef.current) setState(next);
  }, []);

  const upload = useCallback(
    (file: File): void => {
      if (busyRef.current) return;
      send(pageId, file, settle, (): boolean => aliveRef.current);
    },
    [send, pageId, settle],
  );

  return { state, upload };
}

/**
 * The upload of a map's image to ANY page, not the one of a pane: the file goes to /api/upload, then map:set on that
 * page, confirmed by the echo (GK-3: the beat's «new map» window makes the map and gives it the picture at once).
 */
export function useMapImageSender(): (pageId: string, file: File, settle: (state: MapImageUploadState) => void, alive: () => boolean) => void {
  const { mp } = useSessionContext();
  const { t } = useI18n();
  // The caller shows the outcome; Studio's own channel already toasts refusals.
  const commands = useStudioCommands(mp, t, { quiet: true });
  return useCallback(
    (pageId: string, file: File, settle: (state: MapImageUploadState) => void, alive: () => boolean): void => {
      const fileName = file.name;
      const sessionId = mp.session?.id;
      const participantId = mp.participant?.id;
      if (sessionId === undefined || participantId === undefined || mp.status !== 'connected') {
        settle({ status: 'failed', fileName, error: t('session.connectionLost') });
        return;
      }
      settle({ status: 'uploading', fileName });
      const form = new FormData();
      form.append('file', file);
      fetch(
        `/api/upload?sessionId=${encodeURIComponent(sessionId)}&participantId=${encodeURIComponent(participantId)}`,
        { method: 'POST', body: form },
      )
        .then(async (response): Promise<string> => {
          const data = (await response.json().catch((): Record<string, unknown> => ({}))) as { url?: unknown; error?: unknown };
          if (!response.ok || typeof data.url !== 'string') {
            throw new Error(typeof data.error === 'string' && data.error !== '' ? data.error : t('studio.mapImage.uploadFailed'));
          }
          return data.url;
        })
        .then((url: string): void => {
          if (!alive()) return;
          settle({ status: 'saving', fileName });
          commands.create({
            command: 'map:set',
            echo: 'map:changed',
            name: fileName,
            payload: { pageId, url },
            // map:changed carries no requestId: this map, this file.
            matches: (echo) => (echo.pageId === pageId && echo.url === url ? pageId : undefined),
            onCreated: (): void => settle({ status: 'idle' }),
            onFailed: (reason: string): void => settle({ status: 'failed', fileName, error: reason }),
            onUnknown: (): void => settle({ status: 'offline', fileName }),
            // Setting the same image again changes nothing: safe to re-send after the rejoin.
            recover: 'resend',
            alive,
          });
        })
        .catch((error: unknown): void => {
          settle({
            status: 'failed',
            fileName,
            error: error instanceof Error && error.message !== '' ? error.message : t('studio.mapImage.uploadFailed'),
          });
        });
    },
    [mp, t, commands],
  );
}

export interface MapImageControlsProps {
  readonly pageId: string;
  /** The map has an image now (the server's, from the page store). */
  readonly hasImage: boolean;
}

/**
 * The map's image in the Studio map header: «Upload image» while the map has
 * none, a compact «Replace image» once it has one, and next to it where the
 * upload stands (uploading / saving / failed / waiting for the connection).
 */
export function MapImageControls({ pageId, hasImage }: MapImageControlsProps): JSX.Element {
  const { t } = useI18n();
  const { state, upload } = useMapImageUpload(pageId);
  const inputRef = useRef<HTMLInputElement>(null);
  const busy = state.status === 'uploading' || state.status === 'saving' || state.status === 'offline';
  const label = t(hasImage ? 'studio.mapImage.replace' : 'studio.mapImage.upload');

  const note =
    state.status === 'uploading'
      ? t('studio.mapImage.uploading').replace('{name}', state.fileName)
      : state.status === 'saving'
        ? t('studio.mapImage.saving')
        : state.status === 'offline'
          ? t('studio.mapImage.offline')
          : state.status === 'failed'
            ? `${t('studio.mapImage.failed').replace('{name}', state.fileName)}: ${state.error}`
            : undefined;

  return (
    <div className="flex min-w-0 flex-none items-center gap-1.5" data-testid="page-map-image">
      {note !== undefined ? (
        <span
          className={`min-w-0 max-w-[16rem] truncate text-pill ${state.status === 'failed' ? 'text-danger' : 'text-muted-foreground'}`}
          data-testid="page-map-image-status"
          data-status={state.status}
          title={note}
        >
          {note}
        </span>
      ) : null}
      {/* An icon with its words in the hint (group T): «Загрузить изображение» / «Заменить изображение». */}
      <IconButton
        label={label}
        icon={<ImagePlus size={14} aria-hidden="true" />}
        data-testid="page-map-image-button"
        data-has-image={hasImage ? 'true' : 'false'}
        disabled={busy}
        onClick={(): void => inputRef.current?.click()}
      />
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        hidden
        data-testid="page-map-image-input"
        onChange={(e): void => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file !== undefined) upload(file);
        }}
      />
    </div>
  );
}
